import { Spinner, MessageBar, MessageBarBody, Link, Button } from "@fluentui/react-components";
import { Save24Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Processor } from "unified";
import { docLinkToContentName, getCitationFilePath, getHeaders } from "../../api";
import { getToken } from "../../authConfig";
import { useOidc } from "../../oidcContext";
import { MermaidDiagram } from "../Answer/MermaidDiagram";
import { plantumlMindmapToMermaid } from "./plantumlToMermaid";

import styles from "./MarkdownViewer.module.css";

interface MarkdownViewerProps {
    src: string;
    // Called with the /content path of another NextDoc file when the user follows a link to it.
    onNavigate?: (path: string) => void;
}

/**
 * Resolve relative image paths to the /content/ endpoint.
 * Markdown files reference images like "../images/NextDevGoals.png".
 * The flattened image name in Blob Storage is "images__NextDevGoals.png".
 * We derive the sourcefile from the citation URL and resolve relative paths accordingly.
 */
function resolveImageSrc(imgSrc: string, citationSrc: string): string {
    // Absolute URLs or data URIs — pass through
    if (imgSrc.startsWith("http") || imgSrc.startsWith("data:")) {
        return imgSrc;
    }

    // Extract the sourcefile from the citation URL (e.g. "/content/nextdev__index.md")
    const contentPrefix = "/content/";
    const idx = citationSrc.lastIndexOf(contentPrefix);
    if (idx < 0) return imgSrc;

    const sourceFile = citationSrc.substring(idx + contentPrefix.length);
    // Convert flattened name back to path segments: "nextdev__index.md" → ["nextdev", "index.md"]
    const sourceSegments = sourceFile.replace(/\.md$/, "").split("__");

    // Parse the relative image path: "../images/NextDevGoals.png" → ["images", "NextDevGoals.png"]
    const imgParts = imgSrc.split("/");
    // Start from the source file's directory (drop the filename)
    const baseSegments = sourceSegments.slice(0, -1);

    // Resolve relative path components
    const resolved = [...baseSegments];
    for (const part of imgParts) {
        if (part === "..") {
            resolved.pop();
        } else if (part !== ".") {
            resolved.push(part);
        }
    }

    // Flatten to blob storage name: ["images", "NextDevGoals.png"] → "images__NextDevGoals.png"
    const flatName = resolved.join("__");
    return `${contentPrefix}${flatName}`;
}

/**
 * Image component that fetches images via the authenticated /content/ endpoint.
 * Native <img> tags cannot send Authorization headers, so we fetch the blob
 * and create an object URL.
 */
const AuthenticatedImage: React.FC<{ src: string; alt: string; citationSrc: string }> = ({ src, alt, citationSrc }) => {
    const [objectUrl, setObjectUrl] = useState<string>("");
    const { userManager: client } = useOidc();

    useEffect(() => {
        let cancelled = false;
        const resolvedSrc = resolveImageSrc(src, citationSrc);

        // External images can be loaded directly
        if (resolvedSrc.startsWith("http") || resolvedSrc.startsWith("data:")) {
            setObjectUrl(resolvedSrc);
            return;
        }

        const fetchImage = async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await fetch(resolvedSrc, {
                    method: "GET",
                    headers: await getHeaders(token)
                });
                if (!response.ok) return;
                const blob = await response.blob();
                if (!cancelled) {
                    setObjectUrl(URL.createObjectURL(blob));
                }
            } catch {
                // Silently fail for broken images
            }
        };

        fetchImage();
        return () => {
            cancelled = true;
        };
    }, [src, citationSrc]);

    useEffect(() => {
        return () => {
            if (objectUrl && objectUrl.startsWith("blob:")) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    if (!objectUrl) return null;
    return <img src={objectUrl} alt={alt} style={{ maxWidth: "100%" }} />;
};

/**
 * Remove MkDocs-only syntax that plain Markdown renders as noise: the YAML front matter
 * (e.g. "hide: navigation"), Material icon shortcodes such as ":material-account:" and
 * content tab headers (=== "BP1"), which become bold labels.
 */
export function stripMkDocsSyntax(markdown: string): string {
    // Drop the space after an icon too: "-   :icon: **X**" must keep its 3-space gap, else 4-space continuations become code.
    return markdown
        .replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n/, "")
        .replace(/:(material|octicons|fontawesome|simple)-[a-z0-9-]+:[ \t]?/g, "")
        .replace(/^[ \t]*===[+!]?[ \t]+"([^"]*)"[ \t]*$/gm, "**$1**");
}

// NextDoc only uses fenced code; indented lines are leftovers of tabs, admonitions and stripped HTML tables.
function remarkNoIndentedCode(this: Processor) {
    const data = this.data() as { micromarkExtensions?: unknown[] };
    (data.micromarkExtensions ??= []).push({ disable: { null: ["codeIndented"] } });
}

// Derive a sensible download filename from the citation URL. Blob object URLs
// have no filename, so without this the browser saves a suffix-less UUID.
function downloadFileName(src: string): string {
    const withoutHash = src.split("#")[0];
    const lastSegment = withoutHash.split("/").pop() || "download";
    // Ensure a .md extension for markdown citations (eHandbook units resolve to
    // "<unit>.md"; if a name somehow lacks an extension, append .md).
    return lastSegment.includes(".") ? lastSegment : `${lastSegment}.md`;
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ src, onNavigate }) => {
    const [content, setContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [downloadUrl, setDownloadUrl] = useState<string>("");
    const { t } = useTranslation();
    const { userManager: client } = useOidc();

    useEffect(() => {
        const fetchMarkdown = async () => {
            setIsLoading(true);
            setError(null);
            setContent("");

            try {
                const hashIndex = src.indexOf("#");
                const requestUrl = hashIndex >= 0 ? src.substring(0, hashIndex) : src;
                const token = client ? await getToken(client) : undefined;
                const response = await fetch(requestUrl, {
                    method: "GET",
                    headers: await getHeaders(token),
                    cache: "no-store"
                });

                if (!response.ok) {
                    throw new Error("Failed loading markdown file.");
                }

                const markdownBlob = await response.blob();
                const objectUrl = URL.createObjectURL(markdownBlob);
                setDownloadUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev);
                    return objectUrl;
                });

                let markdownText = await markdownBlob.text();
                setContent(stripMkDocsSyntax(markdownText));
            } catch (error: any) {
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMarkdown();
    }, [src]);

    useEffect(() => {
        return () => {
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
            }
        };
    }, [downloadUrl]);

    return (
        <div>
            {isLoading ? (
                <div className={`${styles.loading} ${styles.markdownViewer}`}>
                    <Spinner size="large" label="Loading file" />
                </div>
            ) : error ? (
                <div className={`${styles.error} ${styles.markdownViewer}`}>
                    <MessageBar intent="error">
                        <MessageBarBody>
                            {error.message}
                            <Link href={src} download={downloadFileName(src)}>
                                Download the file
                            </Link>
                        </MessageBarBody>
                    </MessageBar>
                </div>
            ) : (
                <div>
                    <a href={downloadUrl || src} download={downloadFileName(src)} className={styles.downloadButton}>
                        <Button
                            appearance="transparent"
                            style={{ color: "black" }}
                            icon={<Save24Regular />}
                            title={t("tooltips.save")}
                            aria-label={t("tooltips.save")}
                        />
                    </a>
                    <ReactMarkdown
                        children={content}
                        remarkPlugins={[remarkGfm, remarkNoIndentedCode]}
                        className={`${styles.markdown} ${styles.markdownViewer}`}
                        components={{
                            // Relative links would resolve against the chat origin and just reload the chat.
                            a: ({ node, href, children }) => {
                                if (!href || href.startsWith("#")) return <a href={href}>{children}</a>;
                                if (/^(https?:\/\/|mailto:)/i.test(href)) {
                                    return (
                                        <a href={href} target="_blank" rel="noopener noreferrer">
                                            {children}
                                        </a>
                                    );
                                }
                                const baseName = decodeURIComponent(src.split("#")[0].split("/").pop() ?? "");
                                const docName = onNavigate ? docLinkToContentName(href, baseName) : null;
                                if (docName) {
                                    return (
                                        <a
                                            href={getCitationFilePath(docName)}
                                            onClick={e => {
                                                e.preventDefault();
                                                onNavigate?.(getCitationFilePath(docName));
                                            }}
                                        >
                                            {children}
                                        </a>
                                    );
                                }
                                return <span>{children}</span>;
                            },
                            img: ({ node, ...props }) => <AuthenticatedImage src={props.src || ""} alt={props.alt || ""} citationSrc={src} />,
                            code: ({ node, className, children }) => {
                                if (/\blanguage-mermaid\b/.test(className ?? "")) {
                                    return <MermaidDiagram chart={String(children ?? "")} />;
                                }
                                const mindmap = /\blanguage-(puml|plantuml)\b/.test(className ?? "") ? plantumlMindmapToMermaid(String(children ?? "")) : null;
                                return mindmap ? <MermaidDiagram chart={mindmap} /> : <code className={className}>{children}</code>;
                            }
                        }}
                    />
                </div>
            )}
        </div>
    );
};
